import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRecaptchaContext } from '../contexts/RecaptchaContext';

// Type assertion to fix TypeScript compatibility issues with react-google-recaptcha
const ReCAPTCHAComponent = ReCAPTCHA as any;

/**
 * Validates that a node is a valid DOM Node that can be safely observed
 * This prevents MutationObserver errors when browser extensions try to observe invalid nodes
 */
const isValidNode = (node: any): node is Node => {
  const exists = node !== null && node !== undefined && typeof node === 'object';
  if (!exists) return false;
  return node.nodeType === Node.ELEMENT_NODE && node.isConnected !== undefined;
};

/**
 * Validates that document.body is a valid Node before use
 * This prevents MutationObserver errors from browser extensions
 */
const isBodyValid = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }
  const body = document.body;
  return isValidNode(body) && body.isConnected === true;
};

/**
 * Sets body ready state using double requestAnimationFrame to ensure DOM stability
 * This helps prevent browser extensions from trying to observe elements before they're attached
 */
const setBodyReadyWithDoubleRAF = (setIsBodyReady: (ready: boolean) => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Verify body is still valid before setting ready state
      if (isBodyValid()) {
        setIsBodyReady(true);
      }
    });
  });
};

// Custom hook to handle body readiness
const useBodyReadiness = () => {
  const [isBodyReady, setIsBodyReady] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    if (document.body) {
      setBodyReadyWithDoubleRAF(setIsBodyReady);
      return;
    }

    const checkBody = () => {
      if (document.body) {
        setBodyReadyWithDoubleRAF(setIsBodyReady);
      }
    };

    checkBody();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkBody);
      return () => document.removeEventListener('DOMContentLoaded', checkBody);
    }
  }, []);

  return isBodyReady;
};

// Custom hook to handle reCAPTCHA lifecycle
const useRecaptchaLifecycle = (
  recaptchaRef: React.RefObject<any>,
  resetRecaptcha: () => void
) => {
  const resetAnimationFrameRef = useRef<number | null>(null);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (resetAnimationFrameRef.current !== null) {
        cancelAnimationFrame(resetAnimationFrameRef.current);
        resetAnimationFrameRef.current = null;
      }
    };
  }, []);

  // Handle onExpired callback - defer reset to avoid race conditions
  const handleExpired = useCallback(() => {
    console.warn('[RecaptchaRenderer] reCAPTCHA expired');
    if (resetAnimationFrameRef.current !== null) {
      cancelAnimationFrame(resetAnimationFrameRef.current);
    }
    resetAnimationFrameRef.current = requestAnimationFrame(() => {
      resetAnimationFrameRef.current = null;
      resetRecaptcha();
    });
  }, [resetRecaptcha]);

  // Handle onErrored callback
  const handleErrored = useCallback((error: any) => {
    console.error('[RecaptchaRenderer] reCAPTCHA errored:', error);
  }, []);

  // Handle onLoad callback
  const handleLoad = useCallback(() => {
    console.log('[RecaptchaRenderer] reCAPTCHA loaded successfully, ref.current:', !!recaptchaRef?.current);
  }, [recaptchaRef]);

  return { handleExpired, handleErrored, handleLoad };
};

// Custom hook to check if grecaptcha is ready (Enterprise version)
const useGrecaptchaReadiness = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkGrecaptcha = () => {
      // Check for grecaptcha AND grecaptcha.enterprise to ensure we have the correct script
      if ((window as any).grecaptcha && (window as any).grecaptcha.enterprise) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    if (checkGrecaptcha()) return;

    const interval = setInterval(() => {
      if (checkGrecaptcha()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return isReady;
};

// Custom hook to manage the container ref and mounted state
const useRecaptchaContainer = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isContainerMounted, setIsContainerMounted] = useState(false);

  // Extracted callback to reduce complexity of containerRefCallback arrow function
  const handleContainerNode = useCallback((node: HTMLDivElement) => {
    setIsContainerMounted(true);
    
    if (node.parentNode && isValidNode(node.parentNode)) {
      requestAnimationFrame(() => {
        if (node && isValidNode(node) && node.isConnected) {
          // Node is valid and connected, safe for observation
        }
      });
    }
  }, []);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node && isValidNode(node)) {
      handleContainerNode(node);
    } else {
      setIsContainerMounted(false);
    }
  }, [handleContainerNode]);

  return { containerRefCallback, isContainerMounted };
};

// Custom hook to handle reCAPTCHA handlers
const useRecaptchaHandlers = (
  originalHandleLoad: () => void, 
  notifyWidgetLoaded: (() => void) | undefined
) => {
  const handleLoad = useCallback(() => {
    originalHandleLoad();
    if (notifyWidgetLoaded) {
      notifyWidgetLoaded();
    }
  }, [originalHandleLoad, notifyWidgetLoaded]);

  return { handleLoad };
};

const buildDebugInfo = (recaptchaRef: React.RefObject<any>) => {
  const current = recaptchaRef?.current;
  const hasCurrent = !!current;
  return {
    refExists: !!recaptchaRef,
    refCurrent: hasCurrent,
    refType: hasCurrent ? typeof current : 'null',
    hasExecuteAsync: hasCurrent && typeof current?.executeAsync === 'function'
  };
};

const useRecaptchaDebug = (recaptchaRef: React.RefObject<any>) => {
  useEffect(() => {
    console.log('[RecaptchaRenderer] Ref changed:', buildDebugInfo(recaptchaRef));
  });
};

const tryCreatePortal = (element: React.ReactNode): React.ReactPortal | null => {
  const body = document.body;
  if (!isValidNode(body)) {
    console.warn('[RecaptchaRenderer] document.body is not a valid Node, skipping portal creation');
    return null;
  }
  console.log('[RecaptchaRenderer] Rendering via portal to document.body');
  return createPortal(element, body);
};

const logPortalSkipReason = (isBodyReady: boolean): null => {
  const message = !isBodyReady
    ? '[RecaptchaRenderer] Waiting for document.body to be ready'
    : '[RecaptchaRenderer] Environment not suitable for rendering';
  console.log(message);
  return null;
};

const RecaptchaPortal: React.FC<{ element: React.ReactNode; isBodyReady: boolean }> = ({ element, isBodyReady }) => {
  const canRender = typeof document !== "undefined" && isBodyReady && isBodyValid();
  if (!canRender) return logPortalSkipReason(isBodyReady);

  try {
    return tryCreatePortal(element);
  } catch (error) {
    console.error('[RecaptchaRenderer] Failed to create portal:', error);
    return null;
  }
};

const logDisabledReason = (isEnabled: boolean, siteKey: string): null => {
  if (!isEnabled || !siteKey) {
    console.log('[RecaptchaRenderer] Not rendering - disabled or no site key:', { isEnabled, hasSiteKey: !!siteKey });
  }
  return null;
};

function computeShouldRender(isEnabled: boolean, siteKey: string, isGrecaptchaReady: boolean): boolean {
  return isEnabled && siteKey && isGrecaptchaReady ? true : false;
}

function getGrecaptchaInstance(): any {
  return typeof window !== 'undefined' ? (window as any).grecaptcha : undefined;
}

const RecaptchaWidget: React.FC<{
  recaptchaRef: React.RefObject<any>;
  siteKey: string;
  handleErrored: (error: any) => void;
  handleExpired: () => void;
  handleLoad: () => void;
}> = ({ recaptchaRef, siteKey, handleErrored, handleExpired, handleLoad }) => (
  <ReCAPTCHAComponent
    ref={recaptchaRef}
    sitekey={siteKey}
    size="invisible"
    theme="light"
    grecaptcha={getGrecaptchaInstance()}
    onErrored={handleErrored}
    onExpired={handleExpired}
    onLoad={handleLoad}
  />
);

/**
 * RecaptchaRenderer component
 * Renders the reCAPTCHA component at the root level using a portal
 * This decouples reCAPTCHA rendering from individual form components
 */
export const RecaptchaRenderer: React.FC = () => {
  const { recaptchaRef, isEnabled, siteKey, resetRecaptcha, notifyWidgetLoaded } = useRecaptchaContext();
  const isBodyReady = useBodyReadiness();
  const isGrecaptchaReady = useGrecaptchaReadiness();
  
  const { handleExpired, handleErrored, handleLoad: originalHandleLoad } = useRecaptchaLifecycle(recaptchaRef, resetRecaptcha);
  const { handleLoad } = useRecaptchaHandlers(originalHandleLoad, notifyWidgetLoaded);
  const { containerRefCallback, isContainerMounted } = useRecaptchaContainer();
  
  useRecaptchaDebug(recaptchaRef);

  if (!computeShouldRender(isEnabled, siteKey, isGrecaptchaReady)) {
    return logDisabledReason(isEnabled, siteKey);
  }

  const recaptchaElement = (
    <div ref={containerRefCallback} className="recaptcha-container" data-testid="recaptcha-container">
      {isContainerMounted && (
        <RecaptchaWidget
          recaptchaRef={recaptchaRef}
          siteKey={siteKey}
          handleErrored={handleErrored}
          handleExpired={handleExpired}
          handleLoad={handleLoad}
        />
      )}
    </div>
  );

  return <RecaptchaPortal element={recaptchaElement} isBodyReady={isBodyReady} />;
};
